import React, { useState, useEffect } from 'react';
import { Modal, Form, Table, Button, Space, Input, Select, Toast, Popconfirm } from '@douyinfe/semi-ui';
import { IconEyeOpened, IconCode, IconEdit, IconDelete, IconPlus, IconSearch, IconPlayCircle, IconStop } from '@douyinfe/semi-icons';
import { useNavigate } from 'react-router-dom';
import request from '../../utils/request';
import styles from './index.module.scss';

const Pages = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 10, total: 0 });
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [searchKey, setSearchKey] = useState('');
    const [searchStatus, setSearchStatus] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);

    const statusMap = {
        0: { text: '显示', type: 'success' },
        1: { text: '隐藏', type: 'danger' },
    };

    const fetchList = async (page = 1, limit = 10) => {
        setLoading(true);
        try {
            const response = await request.get('/go/sp/all/list_all_page/', {
                params: {
                    page: page - 1,
                    limit,
                    key: searchKey,
                    status: searchStatus === null ? -1 : searchStatus
                }
            });
            if (response.errno === 0) {
                setDataSource(response.list || []);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    total: response.total_cnt
                }));
            } else {
                Toast.error(response.errstr || '获取列表失败');
            }
        } catch (error) {
            console.error('获取列表失败:', error);
            Toast.error('获取列表失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList();
    }, []);

    const handleDelete = async (uids) => {
        try {
            const response = await request.post('/go/sp/all/delete_all_page/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids
            });
            if (response.errno === 0) {
                Toast.success('删除成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(response.errstr || '删除失败');
            }
        } catch (error) {
            console.error('删除失败:', error);
            Toast.error('删除失败');
        }
    };

    const handleBatchEdit = async (status, uids) => {
        if (!uids || (Array.isArray(uids) && !uids.length)) {
            Toast.warning('请选择要操作的页面');
            return;
        }
        try {
            const response = await request.post('/go/sp/all/batchedit_all_page/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids,
                status
            });
            if (response.errno === 0) {
                Toast.success('操作成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(response.errstr || '操作失败');
            }
        } catch (error) {
            console.error('操作失败:', error);
            Toast.error('操作失败');
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '-';
        const date = new Date(parseInt(timestamp));
        return date.toLocaleString();
    };

    const columns = [
        {
            title: '页面信息',
            dataIndex: 'title',
            width: 300,
            render: (text, record) => (
                <div style={{ fontSize: '12px' }}>
                    <div style={{ color: 'var(--semi-color-text-0)', marginBottom: '4px' }}>{text}</div>
                    <div style={{ color: 'var(--semi-color-text-2)' }}>
                        {record.public_info?.name ? `所属小程序：${record.public_info.name}` : '类型：H5页面'}
                    </div>
                    <div style={{ color: 'var(--semi-color-text-2)' }}>分类：{record.typ || '-'}</div>
                </div>
            )
        },
        {
            title: '状态',
            dataIndex: 'status',
            width: 120,
            render: (text) => {
                const status = statusMap[text] || { text: '异常', type: 'danger' };
                return <Button theme='light' type={status.type} size='small'>{status.text}</Button>;
            }
        },
        {
            title: '排序',
            dataIndex: 'sort',
            width: 100
        },
        {
            title: '创建时间/更新时间',
            dataIndex: 'update_time',
            width: 180,
            render: (text, record) => (
                <div style={{ fontSize: '12px', color: 'var(--semi-color-text-2)' }}>
                    <div>{formatTimestamp(record.create_time)}</div>
                    <div>{formatTimestamp(text)}</div>
                </div>
            )
        },
        {
            title: '操作',
            width: 200,
            render: (_, record) => (
                <Space>
                    <Button
                        type='primary'
                        icon={<IconCode />}
                        onClick={() => window.open(`/h5editor?uid=${record.uid}`, '_blank')}
                    >
                        设计页面
                    </Button>
                    <Button
                        type='primary'
                        icon={<IconEyeOpened />}
                        onClick={() => {
                            const token = localStorage.getItem('token');
                            const sp_uid = token ? JSON.parse(token).s : '';
                            window.open(`/preview/h5.html?uid=${record.uid}&sp_uid=${sp_uid}`, '_blank');
                        }}
                    >
                        预览
                    </Button>
                    <Button
                        type='primary'
                        icon={<IconEdit />}
                        onClick={() => {
                            setCurrentRecord(record);
                            setShowEditModal(true);
                        }}
                    >
                        编辑
                    </Button>
                    <Popconfirm
                        title="确定要删除该页面吗？"
                        onConfirm={() => handleDelete(record.uid)}
                    >
                        <Button type='danger' icon={<IconDelete />}>删除</Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <div className={styles.pagesContainer}>
            <div className={styles.header}>
                <Space>
                    <Input
                        placeholder="搜索页面名称"
                        value={searchKey}
                        onChange={setSearchKey}
                        style={{ width: 200 }}
                        showClear
                    />
                    <Select
                        placeholder="页面状态"
                        value={searchStatus}
                        onChange={setSearchStatus}
                        style={{ width: 150 }}
                        showClear
                    >
                        <Select.Option value={null}>全部状态</Select.Option>
                        {Object.entries(statusMap).map(([value, { text }]) => (
                            <Select.Option key={value} value={parseInt(value)}>{text}</Select.Option>
                        ))}
                    </Select>
                    <Button
                        type="primary"
                        onClick={() => fetchList(1, pagination.pageSize)}
                        loading={loading}
                        icon={<IconSearch />}
                    >
                        搜索
                    </Button>
                    <Button
                        type='primary'
                        icon={<IconPlus />}
                        onClick={() => window.open('/h5editor', '_blank')}
                    >
                        新增页面
                    </Button>
                    {selectedRowKeys.length > 0 && (
                        <Space>
                            <Button type='warning' icon={<IconStop />} onClick={() => handleBatchEdit(1, selectedRowKeys)}>批量隐藏</Button>
                            <Button type='primary' icon={<IconPlayCircle />} onClick={() => handleBatchEdit(0, selectedRowKeys)}>批量显示</Button>
                            <Popconfirm
                                title={`确定要删除选中的 ${selectedRowKeys.length} 个页面吗？`}
                                onConfirm={() => handleDelete(selectedRowKeys)}
                            >
                                <Button type='danger' icon={<IconDelete />}>批量删除</Button>
                            </Popconfirm>
                        </Space>
                    )}
                </Space>
            </div>

            <Table
                loading={loading}
                columns={columns}
                dataSource={dataSource}
                pagination={{
                    currentPage: pagination.currentPage,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    onPageChange: (page) => fetchList(page, pagination.pageSize),
                    onPageSizeChange: (size) => fetchList(1, size)
                }}
                rowKey="uid"
                rowSelection={{
                    selectedRowKeys,
                    onChange: setSelectedRowKeys
                }}
            />
            <Modal
                title="编辑页面"
                visible={showEditModal}
                onCancel={() => setShowEditModal(false)}
                footer={null}
                centered
            >
                <Form
                    onSubmit={async (values) => {
                        try {
                            const response = await request.post('/go/sp/all/edit_all_page/', {
                                uid: currentRecord.uid,
                                public_uid: values.public_uid || '0',
                                typ: values.typ,
                                title: values.title,
                                sort: parseInt(values.sort),
                                status: parseInt(values.status)
                            });
                            if (response.errno === 0) {
                                Toast.success('编辑成功');
                                setShowEditModal(false);
                                fetchList();
                            } else {
                                Toast.error(response.errstr || '编辑失败');
                            }
                        } catch (error) {
                            console.error('编辑失败:', error);
                            Toast.error('编辑失败');
                        }
                    }}
                    initValues={{
                        public_uid: currentRecord?.public_uid || '0',
                        typ: currentRecord?.typ,
                        title: currentRecord?.title,
                        sort: currentRecord?.sort,
                        status: currentRecord?.status
                    }}
                >
                    <Form.Input
                        field="title"
                        label="页面名称"
                        placeholder="请输入页面名称"
                        rules={[{ required: true, message: '请输入页面名称' }]}
                    />
                    <Form.Input
                        field="typ"
                        label="分类"
                        placeholder="请输入分类"
                    />
                    <Form.Input
                        field="sort"
                        label="排序"
                        placeholder="请输入排序值（数字越大越靠前）"
                        rules={[{ required: true, message: '请输入排序值' }]}
                    />
                    <Form.Select
                        field="status"
                        label="状态"
                        rules={[{ required: true, message: '请选择状态' }]}
                    >
                        {Object.entries(statusMap).map(([value, { text }]) => (
                            <Form.Select.Option key={value} value={parseInt(value)}>{text}</Form.Select.Option>
                        ))}
                    </Form.Select>
                    <div style={{ marginTop: 24, textAlign: 'right', paddingBottom: 24 }}>
                        <Space>
                            <Button type='tertiary' onClick={() => setShowEditModal(false)}>取消</Button>
                            <Button type='primary' htmlType='submit'>确定</Button>
                        </Space>
                    </div>
                </Form>
            </Modal>
        </div>
    );
};

export default Pages;